import { useCallback, useEffect, useRef, useState } from "react";

// Strip markdown emphasis so nothing is spoken as "asterisk" etc. Belt-and-suspenders
// with the BE plain-text rule — the caption keeps the markdown (rendered as bold).
const stripMarkdownForSpeech = (t) =>
    String(t || "")
        .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
        .replace(/`([^`]*)`/g, "$1") // inline code
        .replace(/\*\*(.*?)\*\*/g, "$1") // bold **
        .replace(/__(.*?)__/g, "$1") // bold __
        .replace(/\*(.*?)\*/g, "$1") // italic *
        .replace(/_(.*?)_/g, "$1") // italic _
        .replace(/^\s{0,3}#{1,6}\s+/gm, "") // leading # headers
        .replace(/[*`]/g, "") // stray emphasis markers
        .trim();

// Plays assistant utterances from the TTS endpoint and exposes a live amplitude
// level (0..1) for the orb. Audio is routed HTMLAudioElement -> MediaElementSource
// -> AnalyserNode -> destination so we can both hear it and read its amplitude.
//
// iOS autoplay: the FIRST real utterance is fetched async (synthesize), and awaiting
// breaks the user-gesture chain. So unlock() must be called SYNCHRONOUSLY inside the
// Begin click — it resumes the AudioContext and primes the <audio> element with a
// silent clip, marking both as user-activated. After that, play() works post-await.

// Build a tiny silent WAV object URL to prime the <audio> element in-gesture
// (avoids shipping a large base64 literal).
const makeSilentWavUrl = () => {
    const sampleRate = 8000;
    const numSamples = Math.floor(sampleRate * 0.05);
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);
    const w = (off, s) => {
        for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
    };
    w(0, "RIFF");
    view.setUint32(4, 36 + numSamples * 2, true);
    w(8, "WAVE");
    w(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    w(36, "data");
    view.setUint32(40, numSamples * 2, true);
    // samples default to 0 → silence
    return URL.createObjectURL(new Blob([view], { type: "audio/wav" }));
};

// At most this many lines' audio held ahead (the next line, plus the interview-2
// opener warming up across a review).
const PREFETCH_MAX = 3;

// `synthesize(text, voice, signal)` is where the audio comes from: a voice
// survey's /collector/tts/, or the builder preview's signed-in route (see
// useVoiceSurveyController). Read through a ref, so a new function identity never
// resets the player.
export default function useTtsPlayback({ synthesize }) {
    const synthesizeRef = useRef(synthesize);
    synthesizeRef.current = synthesize;
    const fetchAudio = useCallback((text, voice, signal) => synthesizeRef.current(text, voice, signal), []);
    const audioElRef = useRef(null);
    const ctxRef = useRef(null);
    const sourceRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const currentUrlRef = useRef(null);
    const silentUrlRef = useRef(null);
    const levelRef = useRef(0); // 0..1 while speaking
    const progressRef = useRef(0); // 0..1 playback position of the current utterance
    const startedRef = useRef(false); // has the current utterance actually begun playing
    const abortRef = useRef(null); // AbortController for the in-flight TTS fetch
    const genRef = useRef(0); // bumped by stop() — invalidates a pending play()
    // Has the CURRENT utterance's audio actually been loaded into the element?
    // Set when play() assigns el.src, cleared by stop() (and so by the next play()).
    // el.src alone can't answer this: stop() leaves the previous blob in place.
    const loadedRef = useRef(false);
    // Prefetched audio, keyed by the exact VOICED text (markdown-stripped — the same
    // string /tts/ is sent): text → { promise, controller }. prefetch() fills it while
    // the respondent is still answering; play() takes from it (and evicts), so a slow
    // synthesis is hidden inside the answer time. Per hook = per session; capped, and
    // only ever holding the next line(s) the BE predicted.
    const cacheRef = useRef(new Map());
    // The clip that was last played, so Replay (and a resume after a pause-veto)
    // replays it without another round-trip.
    const lastClipRef = useRef(null); // { key, blob }

    const ensureAudioEl = useCallback(() => {
        if (!audioElRef.current) {
            const el = new Audio();
            el.preload = "auto";
            audioElRef.current = el;
        }
        return audioElRef.current;
    }, []);

    // The respondent can't hear the voice and the browser won't let us fix
    // that without a fresh gesture: a play() rejected by the autoplay policy
    // (NotAllowedError), or the Web Audio graph the element is routed through sitting
    // suspended. The screen shows a "Tap to enable sound" control while it's true.
    const [blocked, setBlocked] = useState(false);

    // The AudioContext (created on the first gesture, never torn down mid-session).
    const ensureCtx = useCallback(() => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx && !ctxRef.current) ctxRef.current = new AudioCtx();
        } catch (e) {
            /* no-op — playback works via the element alone; level stays 0 */
        }
        return ctxRef.current;
    }, []);

    // Wire element -> analyser -> destination — but ONLY once the context is
    // actually RUNNING. A MediaElementSource takes over the element's output for
    // good (it can't be undone, and only one can exist per element), so routing
    // into a context the browser hasn't let start (Firefox/Safari can hold it
    // 'suspended') makes every line play in total silence, with play() resolving
    // and 'ended' firing as if all were well. Unrouted, the element plays straight
    // to the speakers; all that's lost is the orb's live level.
    const routeIfRunning = useCallback(() => {
        const ctx = ctxRef.current;
        if (!ctx || ctx.state !== "running" || sourceRef.current) return;
        try {
            const el = ensureAudioEl();
            sourceRef.current = ctx.createMediaElementSource(el);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0.7;
            sourceRef.current.connect(analyser);
            analyser.connect(ctx.destination);
            analyserRef.current = analyser;
        } catch (e) {
            /* no-op — playback still works via the element; level stays 0 */
        }
    }, [ensureAudioEl]);

    // Resume the context (call it inside any user gesture — that's what lets it
    // start), then route once it runs. Doesn't touch the element, so it's safe
    // mid-utterance.
    const wake = useCallback(() => {
        const ctx = ensureCtx();
        if (!ctx) return;
        if (ctx.state === "running") {
            routeIfRunning();
            return;
        }
        try {
            const p = ctx.resume();
            if (p && p.then) p.then(routeIfRunning, () => {});
        } catch (e) {
            /* no-op */
        }
    }, [ensureCtx, routeIfRunning]);

    const stopLevelLoop = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        levelRef.current = 0;
    }, []);

    const startLevelLoop = useCallback(() => {
        const analyser = analyserRef.current;
        if (!analyser) return;
        const buf = new Uint8Array(analyser.fftSize);
        const loop = () => {
            const a = analyserRef.current;
            if (!a) return;
            a.getByteTimeDomainData(buf);
            let sumSq = 0;
            for (let i = 0; i < buf.length; i++) {
                const v = (buf[i] - 128) / 128;
                sumSq += v * v;
            }
            levelRef.current = Math.min(1, Math.sqrt(sumSq / buf.length) * 3);
            // Sample the playback position every frame too, so the caption's
            // typewriter moves smoothly between the element's ~4Hz 'timeupdate'
            // events. Same guard as onTime: no real duration yet → leave it.
            const el = audioElRef.current;
            if (el && startedRef.current) {
                const d = el.duration;
                if (d && isFinite(d) && d > 0) progressRef.current = Math.min(1, Math.max(progressRef.current, el.currentTime / d));
            }
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
    }, []);

    // Call synchronously inside the Begin gesture: wakes the AudioContext and
    // play()s a silent clip on THE element every line later plays through, which
    // is what unlocks it for the autoplay policy (Safari/iOS unlock per element).
    // Replaces the element's source — so only when nothing's playing.
    const unlock = useCallback(() => {
        try {
            wake();
            const el = ensureAudioEl();
            if (!silentUrlRef.current) silentUrlRef.current = makeSilentWavUrl();
            el.src = silentUrlRef.current;
            const p = el.play();
            if (p && p.catch) p.catch(() => {});
        } catch (e) {
            /* no-op */
        }
    }, [wake, ensureAudioEl]);

    // Pause the current utterance (keeps the play() promise pending — 'ended' only
    // fires on natural completion, so resume() continues to it).
    const pause = useCallback(() => {
        const el = audioElRef.current;
        if (el && !el.paused) {
            try {
                el.pause();
            } catch (e) {
                /* no-op */
            }
        }
        stopLevelLoop();
    }, [stopLevelLoop]);

    const resume = useCallback(() => {
        const el = audioElRef.current;
        if (el && el.paused && el.src) {
            if (ctxRef.current && ctxRef.current.state === "suspended") {
                try {
                    ctxRef.current.resume();
                } catch (e) {
                    /* no-op */
                }
            }
            const p = el.play();
            if (p && p.catch) p.catch(() => {});
            startLevelLoop();
        }
    }, [startLevelLoop]);

    // Stop whatever is playing without tearing the graph down (replay / new turn /
    // Skip intro). This is a HARD stop, in three parts:
    //   1. bump the generation, which invalidates any play() still awaiting its
    //      fetch — that call can no longer touch the element (the Skip-intro race:
    //      the intro audio used to arrive a moment after the skip and play anyway);
    //   2. abort the in-flight synthesis so we don't pay for audio nobody hears;
    //   3. pause AND rewind the element, and clear the started/progress refs the
    //      caption + orb read, so nothing reads as mid-utterance.
    const stop = useCallback(() => {
        genRef.current += 1;
        if (abortRef.current) {
            try {
                abortRef.current.abort();
            } catch (e) {
                /* no-op */
            }
            abortRef.current = null;
        }
        const el = audioElRef.current;
        if (el) {
            try {
                el.pause();
                el.currentTime = 0;
            } catch (e) {
                /* no-op */
            }
        }
        startedRef.current = false;
        progressRef.current = 0;
        loadedRef.current = false;
        stopLevelLoop();
    }, [stopLevelLoop]);

    // True when the element is holding an utterance that hasn't finished — i.e.
    // resume() would continue it. False when the current utterance never made it
    // into the element (superseded, aborted, or vetoed before playback), where
    // resume() would otherwise restart whatever blob was left behind. The caller
    // uses this to choose between un-pausing and re-speaking.
    const isMidUtterance = useCallback(() => {
        const el = audioElRef.current;
        return !!(loadedRef.current && el && !el.ended);
    }, []);

    // Start fetching `text`'s audio now, for a play() later. No-op when it's already
    // cached / in flight, or is the clip just played. A failed prefetch is just a
    // miss: play() then fetches as usual. Returns a promise that settles once the
    // audio is in hand (or has failed), so a caller can wait for the voice to be
    // ready before showing the line — it never rejects.
    const prefetch = useCallback(
        (text, voice) => {
            const key = stripMarkdownForSpeech(text);
            if (!key) return Promise.resolve();
            const cache = cacheRef.current;
            if (lastClipRef.current && lastClipRef.current.key === key) return Promise.resolve();
            if (cache.has(key)) return cache.get(key).promise.then(() => undefined, () => undefined);
            const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
            // Never let a prefetch throw into its caller (it runs inside /start/ and
            // the turn handlers): any failure, sync or async, is just a cache miss.
            let promise;
            try {
                promise = Promise.resolve(fetchAudio(key, voice, controller?.signal));
            } catch (e) {
                return Promise.resolve();
            }
            const entry = { promise, controller };
            promise.catch(() => {
                if (cache.get(key) === entry) cache.delete(key);
            });
            cache.set(key, entry);
            while (cache.size > PREFETCH_MAX) {
                const [oldKey, old] = cache.entries().next().value;
                try {
                    old.controller && old.controller.abort();
                } catch (e) {
                    /* no-op */
                }
                cache.delete(oldKey);
            }
            return promise.then(() => undefined, () => undefined);
        },
        [fetchAudio]
    );

    // Fetch + play one utterance. Resolves on 'ended'; rejects on fetch/playback
    // error (the synthesize call attaches err.code so the caller can detect a lock).
    //
    // `opts.canPlay` is an optional veto evaluated once, AFTER the audio has been
    // synthesized and immediately before playback starts — the intro passes a
    // "still-intro-and-not-skipped" check there. A superseded (stop()'d), aborted
    // or vetoed utterance settles the promise NEITHER way: the flow that queued it
    // is no longer the live one, so it must not advance either.
    const play = useCallback(
        (text, voice, opts) =>
            new Promise((resolve, reject) => {
                (async () => {
                    stop(); // supersede whatever is playing (also bumps the generation)
                    const gen = genRef.current;
                    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
                    abortRef.current = controller;
                    // Reset per-utterance progress; caption stays hidden until 'playing'.
                    progressRef.current = 0;
                    startedRef.current = false;
                    const key = stripMarkdownForSpeech(text);
                    let blob = null;
                    // 1. The clip just played (Replay) → no round-trip at all.
                    // 2. A prefetched clip — finished, or still in flight (awaited, not
                    //    re-requested). Evicted on use. A failed prefetch falls through.
                    // 3. Otherwise fetch it now.
                    const cached = cacheRef.current.get(key);
                    if (cached) cacheRef.current.delete(key);
                    if (lastClipRef.current && lastClipRef.current.key === key) {
                        blob = lastClipRef.current.blob;
                    } else if (cached) {
                        try {
                            blob = await cached.promise;
                        } catch (e) {
                            blob = null;
                        }
                        if (gen !== genRef.current) return; // superseded while waiting on it
                        if (!blob || !blob.size) blob = null;
                    }
                    if (!blob) {
                        try {
                            blob = await fetchAudio(key, voice, controller?.signal);
                        } catch (e) {
                            if (gen !== genRef.current || e?.name === "AbortError") return;
                            reject(e);
                            return;
                        }
                    }
                    if (gen !== genRef.current) return; // stopped/skipped while synthesizing
                    // An empty body is no audio at all — fail it like a 502, so the
                    // caller falls back (text typed at the fixed pace, turn opens).
                    if (!blob || !blob.size) {
                        reject(new Error("Empty audio"));
                        return;
                    }
                    if (opts && typeof opts.canPlay === "function" && !opts.canPlay()) return;
                    if (abortRef.current === controller) abortRef.current = null;
                    lastClipRef.current = { key, blob };
                    const el = ensureAudioEl();
                    const url = URL.createObjectURL(blob);
                    if (currentUrlRef.current) URL.revokeObjectURL(currentUrlRef.current);
                    currentUrlRef.current = url;

                    const onPlaying = () => {
                        startedRef.current = true;
                    };
                    // currentTime / duration → progressRef, which drives the caption's
                    // typewriter. `duration` is NaN (or Infinity, for a stream) until
                    // metadata loads; until then progress simply isn't written.
                    const onTime = () => {
                        const d = el.duration;
                        if (d && isFinite(d) && d > 0) progressRef.current = Math.min(1, el.currentTime / d);
                    };
                    const cleanup = () => {
                        el.removeEventListener("ended", onEnded);
                        el.removeEventListener("error", onError);
                        el.removeEventListener("playing", onPlaying);
                        el.removeEventListener("timeupdate", onTime);
                    };
                    const onEnded = () => {
                        progressRef.current = 1;
                        cleanup();
                        stopLevelLoop();
                        resolve();
                    };
                    const onError = () => {
                        cleanup();
                        stopLevelLoop();
                        reject(new Error("Audio playback failed"));
                    };
                    el.addEventListener("ended", onEnded, { once: true });
                    el.addEventListener("error", onError, { once: true });
                    el.addEventListener("playing", onPlaying);
                    el.addEventListener("timeupdate", onTime);

                    el.muted = false;
                    el.src = url;
                    loadedRef.current = true;
                    // Route through the analyser if the context runs; if the element
                    // is ALREADY routed and the context isn't running, this line
                    // would be silent — say so (the screen offers "enable sound"),
                    // and keep going so the text and the turn flow aren't held up.
                    routeIfRunning();
                    const ctx = ctxRef.current;
                    if (sourceRef.current && ctx && ctx.state !== "running") {
                        try {
                            const r = ctx.resume();
                            if (r && r.catch) r.catch(() => {});
                        } catch (e) {
                            /* no-op */
                        }
                        // eslint-disable-next-line no-console
                        console.warn("[tts] audio context is", ctx.state, "— this line is inaudible until a tap enables sound");
                        setBlocked(true);
                    }
                    try {
                        await el.play();
                        startLevelLoop();
                    } catch (e) {
                        cleanup();
                        stopLevelLoop();
                        if (e && e.name === "NotAllowedError") {
                            // The autoplay policy refused this line. Logged for
                            // diagnosis; the caller still falls back (text, turn opens).
                            // eslint-disable-next-line no-console
                            console.warn("[tts] play() blocked by the autoplay policy:", e.name, e.message);
                            setBlocked(true);
                        }
                        reject(e);
                    }
                })();
            }),
        [fetchAudio, ensureAudioEl, stop, startLevelLoop, stopLevelLoop, routeIfRunning]
    );

    // Full teardown on unmount.
    useEffect(() => {
        const cache = cacheRef.current; // the Map itself never changes identity
        return () => {
            stopLevelLoop();
            genRef.current += 1; // invalidate any pending play()
            if (abortRef.current) {
                try {
                    abortRef.current.abort();
                } catch (e) {
                    /* no-op */
                }
                abortRef.current = null;
            }
            cache.forEach((entry) => {
                try {
                    entry.controller && entry.controller.abort();
                } catch (e) {
                    /* no-op */
                }
            });
            cache.clear();
            lastClipRef.current = null;
            const el = audioElRef.current;
            if (el) {
                try {
                    el.pause();
                    el.src = "";
                } catch (e) {
                    /* no-op */
                }
            }
            if (currentUrlRef.current) {
                URL.revokeObjectURL(currentUrlRef.current);
                currentUrlRef.current = null;
            }
            if (silentUrlRef.current) {
                URL.revokeObjectURL(silentUrlRef.current);
                silentUrlRef.current = null;
            }
            if (ctxRef.current) {
                try {
                    ctxRef.current.close();
                } catch (e) {
                    /* no-op */
                }
                ctxRef.current = null;
            }
            analyserRef.current = null;
            sourceRef.current = null;
        };
    }, [stopLevelLoop]);

    // analyserRef exposes the live AnalyserNode so a visualizer can draw a real
    // waveform (null until the first play wires the graph). progressRef/startedRef
    // let the caption reveal in sync with the audio.
    // Once audio is audible again (context running), drop the "enable sound" prompt.
    const clearBlocked = useCallback(() => setBlocked(false), []);

    return { unlock, wake, blocked, clearBlocked, play, prefetch, stop, pause, resume, isMidUtterance, levelRef, analyserRef, progressRef, startedRef };
}
